// src/pages/imdf/list.tsx

import React, { useRef, useState } from 'react';
import { useList, useCreate } from "@refinedev/core";
import { Link } from "react-router-dom";
import { usePermission } from '../../components/PermissionGuard';

interface IMDFFile {
  id: string;
  originalName: string;
  storagePath: string;
  description?: string;
  version?: string;
  category?: string;
  uploadedBy?: string;
  fileSize: number;
  status: 'pending' | 'validating' | 'valid' | 'invalid';
  createdAt: string;
  updatedAt: string;
}

const IMDFListPage: React.FC = () => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const { isMaintainer } = usePermission();
  
  const { data, isLoading, error, refetch } = useList<IMDFFile>({
    resource: "imdf",
    config: {
      pagination: {
        mode: "off"
      }
    }
  });

  const { mutate: createIMDF } = useCreate();

  const files = data?.data ?? [];

  const formatFileSize = (bytes: number): string => {
    const sizes = ['B', 'KB', 'MB', 'GB'];
    let i = 0;
    let size = bytes;
    while (size >= 1024 && i < sizes.length - 1) {
      size /= 1024;
      i++;
    }
    return `${size.toFixed(1)} ${sizes[i]}`;
  };

  const getStatusClasses = (status: IMDFFile['status']): string => {
    const statusClasses: Record<IMDFFile['status'], string> = {
      valid: "bg-green-100 text-green-800",
      invalid: "bg-red-100 text-red-800",
      pending: "bg-yellow-100 text-yellow-800",
      validating: "bg-blue-100 text-blue-800",
    };
    return `px-2 py-1 rounded-full text-xs font-medium ${statusClasses[status]}`;
  };

  const handleViewClick = (id: string): void => {
    window.location.href = `/imdf/view/${id}`;
  };

  const handleDownloadClick = async (id: string): Promise<void> => {
    try {
      const API_URL = import.meta.env.VITE_API_URL || 'http://imdf.sto-tek.com';
      const token = localStorage.getItem('token');
      
      const response = await fetch(`${API_URL}/imdf/files/${id}/download`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error('下載失敗');
      }

      // 取得檔案名稱
      const contentDisposition = response.headers.get('Content-Disposition');
      let filename = 'download.zip';
      if (contentDisposition) {
        const matches = contentDisposition.match(/filename="(.+)"/);
        if (matches) {
          filename = decodeURIComponent(matches[1]);
        }
      }

      // 下載檔案
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (error) {
      console.error('下載檔案失敗:', error);
      alert('下載檔案失敗，請稍後再試');
    }
  };

  // 處理上傳按鈕點擊
  const handleUploadClick = (): void => {
    // 重置錯誤訊息
    setUploadError(null);
    // 觸發隱藏的檔案輸入框點擊
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  // 處理檔案選擇
  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>): Promise<void> => {
    const selectedFile = e.target.files?.[0];
    if (!selectedFile) return;

    // 檢查檔案類型
    if (selectedFile.type !== 'application/zip' && !selectedFile.name.endsWith('.zip')) {
      setUploadError('只允許上傳 ZIP 檔案');
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
      return;
    }

    // 檢查檔案大小
    if (selectedFile.size > 100 * 1024 * 1024) { // 100MB
      setUploadError('檔案大小不能超過 100MB');
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
      return;
    }

    setIsUploading(true);
    setUploadError(null);

    try {
      // 建立 FormData 物件
      const formData = new FormData();
      formData.append('file', selectedFile);
      
      // 使用 Refine 的 useCreate Hook 上傳檔案
      await createIMDF(
        {
          resource: "imdf",
          // 注意：根據 API 路徑對照表，這裡不需要指定完整路徑，只需指定資源類型
          // DataProvider 會自動處理路徑映射
          values: formData,
          meta: {
            headers: {
              'Content-Type': 'multipart/form-data',
            },
          },
        },
        {
          onSuccess: () => {
            // 重新獲取檔案列表
            refetch();
            // 顯示成功訊息
            alert('檔案上傳成功');
          },
          onError: (error) => {
            console.error('上傳檔案失敗', error);
            setUploadError('上傳檔案失敗，請稍後再試');
          },
        }
      );
    } catch (error) {
      console.error('上傳檔案時發生錯誤', error);
      setUploadError('上傳檔案時發生錯誤，請稍後再試');
    } finally {
      setIsUploading(false);
      // 重置檔案輸入框
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-32">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-8 text-center text-gray-500">
        無法載入資料，請稍後再試
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow-sm p-6">
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-xl font-bold text-gray-900">IMDF 檔案管理</h2>
        <div className="flex gap-3">
          {/* 隱藏的檔案輸入框 */}
          <input
            type="file"
            ref={fileInputRef}
            onChange={handleFileChange}
            accept=".zip"
            className="hidden"
          />
          <button
            onClick={handleUploadClick}
            disabled={isUploading}
            className={`px-4 py-2 bg-blue-600 text-white font-medium rounded-md hover:bg-blue-700 transition-colors ${
              isUploading ? 'opacity-70 cursor-not-allowed' : ''
            }`}
          >
            {isUploading ? '上傳中...' : '上傳新檔案'}
          </button>
        </div>
      </div>
      
      {/* 錯誤訊息顯示 */}
      {uploadError && (
        <div className="mb-4 p-3 bg-red-100 border-l-4 border-red-500 text-red-700">
          <p>{uploadError}</p>
        </div>
      )}
      
      <div className="overflow-x-auto">
        {files.length === 0 ? (
          <div className="p-8 text-center">
            <p className="text-gray-500">目前沒有 IMDF 檔案</p>
            <p className="text-sm text-gray-400 mt-2">點擊上方的「上傳新檔案」按鈕來上傳檔案</p>
          </div>
        ) : (
          <table className="w-full">
            <thead>
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider bg-gray-50 border-b">
                  檔案名稱
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider bg-gray-50 border-b">
                  版本
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider bg-gray-50 border-b">
                  分類
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider bg-gray-50 border-b">
                  狀態
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider bg-gray-50 border-b">
                  大小
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider bg-gray-50 border-b">
                  操作
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {files.map((file) => (
                <tr key={file.id} className="hover:bg-gray-50">
                  <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                    {file.originalName}
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                    {file.version || '-'}
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                    {file.category || '-'}
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap text-sm">
                    <span className={getStatusClasses(file.status)}>
                      {file.status}
                    </span>
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                    {formatFileSize(file.fileSize)}
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap text-sm">
                    <div className="flex gap-2">
                      <button
                        onClick={() => handleViewClick(file.id)}
                        className="px-3 py-1 text-sm font-medium text-blue-600 hover:text-blue-800 border border-blue-600 hover:border-blue-800 rounded-md transition-colors"
                      >
                        查看
                      </button>
                      {isMaintainer() && (
                        <Link
                          to={`/imdf/edit/${file.id}`}
                          className="px-3 py-1 text-sm font-medium text-green-600 hover:text-green-800 border border-green-600 hover:border-green-800 rounded-md transition-colors"
                        >
                          編輯
                        </Link>
                      )}
                      <button
                        onClick={() => handleDownloadClick(file.id)}
                        className="px-3 py-1 text-sm font-medium text-gray-600 hover:text-gray-800 border border-gray-600 hover:border-gray-800 rounded-md transition-colors"
                      >
                        下載
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};

export default IMDFListPage;